import React, { Suspense, useRef, useState } from "react"
import { AppLayout } from "app/core/layouts/AppLayout"
import { BlitzPage, Link, getAntiCSRFToken, invalidateQuery } from "blitz"
import Loading from "app/core/components/Loading"
import { PageHeader } from "app/core/components/PageHeader"
import { TestResultsList } from "app/test-results/components/TestResultsList"
import getTestResults from "app/test-results/queries/getTestResults"
import { Button } from "app/core/components/Button"
import Spinner from "react-md-spinner"
import { toast } from "react-hot-toast"
import { DialogOverlay, DialogContent } from "@reach/dialog"
import { TestFluencyResultsList } from "app/test-results/components/TestFluencyResultsList"
import clsx from "clsx"
import { Line } from "recharts"
import { HiArrowCircleLeft } from "react-icons/hi"
import { ButtonLink } from "app/core/components/ButtonLink"

type UploadResult = {
  createdReports: number
  updatedReports: number
  failedReports: any
}

export const TabsComponent = ({ admin = false }) => {
  const [selectedLists, setSelectedLists] = useState("Growth")
  return (
    <div>
      <div className="relative self-center bg-gray-100 rounded-lg p-0.5 flex">
        <button
          onClick={() => setSelectedLists("Growth")}
          type="button"
          className={clsx(
            "flex-1 relative border-gray-200 rounded-md  py-2 text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-primary-300 focus:ring-primary-500 focus:z-10 active:bg-gray-50 active:text-gray-800 transition ease-in-out duration-150 px-3 ",
            selectedLists === "Growth" && "bg-white shadow-sm"
          )}
          style={{ maxWidth: "50%" }}
        >
          MAP Growth Results
        </button>
        <button
          onClick={() => setSelectedLists("Fluency")}
          type="button"
          className={clsx(
            "flex-1 relative border border-transparent rounded-md py-2 text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-primary-300 focus:ring-primary-500 focus:z-10 active:bg-gray-50 active:text-gray-800 transition ease-in-out duration-150 px-3",
            selectedLists === "Fluency" && "bg-white shadow-sm"
          )}
          style={{ maxWidth: "50%" }}
        >
          MAP Fluency Results
        </button>
      </div>

      {selectedLists === "Growth" ? (
        <TestResultsList admin={admin} />
      ) : (
        <TestFluencyResultsList admin={admin} />
      )}
    </div>
  )
}

const AdminTestResultsPage: BlitzPage = () => {
  const [loading, setLoading] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const [errors, setErrors] = useState<any>("")
  const fileRef = useRef<HTMLInputElement>(null)
  const fluencyFileRef = useRef<HTMLInputElement>(null)
  const handleUpload = async (e) => {
    setLoading(true)
    if (fileRef.current) {
      const data = new FormData()
      const files = e.target.files

      if (files.length) {
        data.append("file", files[0])
        let options = {
          method: "POST",
          body: data,
          headers: {},
        }
        const antiCSRFToken = getAntiCSRFToken()
        if (antiCSRFToken) options.headers["anti-csrf"] = antiCSRFToken
        try {
          fetch("/api/test-results/uploadTestResult", options)
          // const {
          //   createdReports,
          //   updatedReports,
          //   failedReports,
          // }: UploadResult = await result.json()
          fileRef.current.value = ""
          setLoading(false)
          // if (failedReports.length) {
          //   setErrors(failedReports)
          //   setShowDialog(true)
          // }
          toast.success(() => <div>File is being processed, check back in a couple minutes.</div>)
          invalidateQuery(getTestResults)
        } catch (error) {
          setLoading(false)
          toast.error(`Error uploading test results: ${error.toString()}`)
        }
      }
    }
  }
  const handleFluencyUpload = async (e) => {
    setLoading(true)
    if (fluencyFileRef.current) {
      const data = new FormData()
      const files = e.target.files

      if (files.length) {
        data.append("file", files[0])
        let options = {
          method: "POST",
          body: data,
          headers: {},
        }
        const antiCSRFToken = getAntiCSRFToken()
        if (antiCSRFToken) options.headers["anti-csrf"] = antiCSRFToken
        try {
          const result = await fetch("/api/test-results/uploadFluencyTestResult", options)
          const {
            createdReports,
            updatedReports,
            failedReports,
          }: UploadResult = await result.json()
          fluencyFileRef.current.value = ""
          setLoading(false)
          if (failedReports.length) {
            setErrors(failedReports)
            setShowDialog(true)
          }
          toast.success(() => (
            <div>
              <div>{createdReports} created</div>
              <div>{updatedReports} updated</div>
            </div>
          ))
          invalidateQuery(getTestResults)
        } catch (error) {
          setLoading(false)
          toast.error(`Error uploading test results: ${error.toString()}`)
        }
      }
    }
  }
  const buttonTextClass = loading ? "invisible" : "visible"
  const spinnerClass = loading ? "visible" : "invisible"

  const errorsTitle = errors.length === 1 ? "1 Error" : `${errors.length} Errors`

  return (
    <div className="md:py-6 px-4 pt-2 pb-6 sm:px-6 lg:px-8 space-y-4">
      <DialogOverlay
        isOpen={showDialog}
        onDismiss={() => setShowDialog(false)}
        className="modal--small"
        aria-label="Error popup"
      >
        <DialogContent>
          <div className="overflow-y-auto max-h-80 px-4">
            <h3 className="font-bold text-xl mb-2 pb-2 border-b border-gray-200">{errorsTitle}</h3>
            <div>
              {errors.length &&
                errors.map(({ error, studentId }) => (
                  <div key={studentId} className="mb-3">
                    <div className="font-semibold">Student ID: {studentId}</div>
                    <div>{error.message}</div>
                  </div>
                ))}
            </div>
          </div>
        </DialogContent>
      </DialogOverlay>
      <div className="flex justify-between">
        <PageHeader title="Admin Test Results" />
        <div className="flex">
          <div className="mr-2">
            <input type="file" ref={fileRef} className="hidden" onChange={handleUpload} />
            <Button
              type="button"
              onClick={(e) => fileRef.current && fileRef.current.click()}
              className="relative"
            >
              <span className={buttonTextClass}>Upload NWEA CSV</span>
              <div className="absolute left-1/2 transform -translate-x-1/2">
                <Spinner className={spinnerClass} size={26} singleColor="#ffffff" />
              </div>
            </Button>
          </div>
          <div className="mr-2">
            <input
              type="file"
              ref={fluencyFileRef}
              className="hidden"
              onChange={handleFluencyUpload}
            />
            <Button
              type="button"
              onClick={(e) => fluencyFileRef.current && fluencyFileRef.current.click()}
              className="relative"
            >
              <span className={buttonTextClass}>Upload NWEA Fluency CSV</span>
              <div className="absolute left-1/2 transform -translate-x-1/2">
                <Spinner className={spinnerClass} size={26} singleColor="#ffffff" />
              </div>
            </Button>
          </div>
          <div>
            <ButtonLink href="/admin/test-results/progress">Test Report Audit</ButtonLink>
          </div>
        </div>
      </div>
      <Suspense fallback={<Loading />}>
        <TabsComponent admin={true} />
      </Suspense>
    </div>
  )
}

AdminTestResultsPage.authenticate = true
AdminTestResultsPage.getLayout = (page) => <AppLayout>{page}</AppLayout>

export default AdminTestResultsPage
